import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import * as patientActions from "../../core/actions/patientActions";
import { patientServices } from "../../core/services";
import Breadcrumbs from "../erp-billing/Breadcrumbs";
import SignaturePad from "./SignaturePad";
import PrintPanchkarmaConsent from "../patient/print/PrintPanchkarmaConsent";
import personDetailsService from "../../core/services/personDetailsService";
import {
  ConsentFormStatus,
  ConsentTemplateType,
  FormGroupName,
} from "../../shared/constants";
import { helperServices } from "../../shared/helper";


const todayStr = () => new Date().toISOString().slice(0, 10);
const emptyEntry = () => ({ procedureName: "", takenBy: "", date: todayStr() });
const parseTemplateContent = (content) => {
  if (!content) return [];

  try {
    const parsed = JSON.parse(content);
    return Array.isArray(parsed) ? parsed : [String(parsed)];
  } catch (error) {
    return [String(content)];
  }
};

const PanchakarmaConsentForm = (props) => {
  const dispatch = useDispatch();
  const patientId = props.match.params.patientid;
  const visitId = props.match.params.visitid; 
  const subgroup = props.match.params.subgroup;
  const groupname = FormGroupName.Panchakarma_Consent_Form;
  const templateType = ConsentTemplateType.Panchakarma_Consent_Form;
  // const subgroup = "panchakarma_consent";
  const isNewMode = new URLSearchParams(props.location?.search || "").get("mode")==="new"; 

  const [templates, setTemplates] = useState({
    en: {
      id: null,
      version: null,
      content: [],
    },
    kn: {
      id: null,
      version: null,
      content: [],
    },
  });

  const [templateLoading, setTemplateLoading] = useState(true);
  const [templateError, setTemplateError] = useState("");

  const [header, setHeader] = useState({ patientName: "", opdIpdNo: "", doctorName: "" , age: "", gender: "" });
  const [language, setLanguage] = useState("en");
  const [activeEntry, setActiveEntry] = useState(emptyEntry());
  const [persistedRecords, setPersistedRecords] = useState([]);
  const [stagedRecords, setStagedRecords] = useState([]);
  const sharedSigRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const [showPrint, setShowPrint] = useState(false);
  const [alreadySaved, setAlreadySaved] = useState(false);
  const [formStatus, setFormStatus] = useState(ConsentFormStatus.inprogress);
  const existingRowIdsRef = useRef({});

  useEffect(() => {

    patientServices
      .getFormTextContent(templateType)
      .then((res) => {
        console.log("Panchakarma form text API response:", res);

        if (!Array.isArray(res)) {
          setTemplateError("Invalid form text response");
          setTemplateLoading(false);
          return; 
        }

        const englishRow = res.find(
          (item) => item.language === "EN"
        );

        const kannadaRow = res.find(
          (item) => item.language === "KN"
        );

        try {
          const englishContent = englishRow?.content
            ? parseTemplateContent(englishRow.content)
            : [];

          const kannadaContent = kannadaRow?.content
            ? parseTemplateContent(kannadaRow.content)
            : [];

          setTemplates({
            en: {
              id: englishRow?.id ?? null,
              version: englishRow?.version ?? null,
              content: englishContent,
            },
            kn: {
              id: kannadaRow?.id ?? null,
              version: kannadaRow?.version ?? null,
              content: kannadaContent,
            },
          });

          setTemplateError("");
        } catch (parseError) {
          console.error(
            "Failed to parse Panchakarma form content:",
            parseError
          );

          setTemplateError(
            "Invalid Panchakarma consent form content"
          );
        } finally {
          setTemplateLoading(false);
        }
      })
      .catch((err) => {
        console.error(
          "Failed to load Panchakarma form text:",
          err
        );

        setTemplateError(
          "Failed to load Panchakarma consent form text"
        );

        setTemplateLoading(false);
      });

    dispatch(
      patientActions.getPatientProfile(patientId, visitId, (resCb) => {
        if (!resCb) return;
        setHeader((prev) => ({
          ...prev,
          patientName: `${resCb.fname || ""} ${resCb.mname || ""} ${resCb.lname || ""}`
            .replace(/\s+/g, " ")
            .trim(),
          age: resCb.age || "", 
          gender: resCb.gender || "",
          opdIpdNo: resCb.visit?.ipopnumber || resCb.ipopnumber || "",
        })); 
      })
    );

    dispatch(
      patientActions.getVisitListById(visitId, "showAllVisits", (resCb) => {
        const visitListData = resCb?.patsearchreslt || [];
        const firstVisit = visitListData[0];
        if (!firstVisit) return;
        const currentVisitAssignments = firstVisit?.visitAssignmentList || [];
        const personLookup = {};
        if (Array.isArray(resCb?.personList)) {
          resCb.personList.forEach((person) => {
            if (person?.personId != null) personLookup[person.personId] = person;
          });
        }
        const latestDoctorAssignment = [...currentVisitAssignments]
          .reverse()
          .find((a) => a.groupname == null && a.targetid && a.status === "open");
        let doctor = null;
        if (latestDoctorAssignment) doctor = personLookup[latestDoctorAssignment.targetid];
        if (!doctor && latestDoctorAssignment?.targetid) {
          const doctorsDetail = personDetailsService.getDoctorsDetail();
          if (Array.isArray(doctorsDetail)) {
            const foundDoctor = doctorsDetail.find(
              (doc) =>
                doc &&
                (doc.personId == latestDoctorAssignment.targetid ||
                  doc.gvId == latestDoctorAssignment.targetid ||
                  doc.gvid == latestDoctorAssignment.targetid)
            );
            if (foundDoctor) {
              doctor = {
                first_name: foundDoctor.first_name || foundDoctor.firstName || "",
                last_name: foundDoctor.last_name || foundDoctor.lastName || "",
                displaytitle: foundDoctor.displaytitle || foundDoctor.title || "",
              };
            }
          }
        }
        if (!doctor && firstVisit.person) doctor = firstVisit.person;
        if (doctor) {
          const doctorName = `${doctor.displaytitle || ""} ${doctor.first_name || ""} ${doctor.last_name || ""}`.trim();
          setHeader((prev) => ({ ...prev, doctorName: doctorName || prev.doctorName }));
        }
      })
    );

    // Add this near the top of the component, after patientId/visitId/groupname are defined:
    // const isNewMode = new URLSearchParams(props.location?.search || "").get("mode") === "new";
    if (!isNewMode) {
      patientServices
        .getDischargeSummary({}, { patientId, visitId, groupname, subgroup })
        .then((resCb) => {
          const list = resCb?.procedureList;
          if (!Array.isArray(list) || list.length === 0) return;
          setAlreadySaved(true);
          const byClass = {};
          list.forEach((row) => {
            byClass[row.claSS] = row.notes;
            if (row.id != null) existingRowIdsRef.current[row.claSS] = row.id;
          });
          setFormStatus(byClass.status || ConsentFormStatus.inprogress);
          setHeader((prev) => ({
            ...prev,
            patientName: byClass.PatientName || prev.patientName,
            opdIpdNo: byClass.OpdIpdNo || prev.opdIpdNo,
            doctorName: byClass.TreatingDoctor || prev.doctorName,
          }));
          if (byClass.PanchakarmaProcedures) {
            try {
              const saved = JSON.parse(byClass.PanchakarmaProcedures);
              setPersistedRecords(
                saved.map((r) => ({
                  ...r,
                  sigUrl: r.signature || null,
                }))
              ); 
            } catch (e) {
              console.error("Failed to parse saved Panchakarma records:", e);
            }
          }
        })
        .catch(() => {});
    } else {
      // Fresh form: clear anything a previous save might have left behind
      setAlreadySaved(false);
      setFormStatus(ConsentFormStatus.inprogress);
      existingRowIdsRef.current = {};
      setPersistedRecords([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


  const fillPanchakarmaTemplate = (paragraphs) =>
    (Array.isArray(paragraphs) ? paragraphs : [paragraphs]).map((paragraph) =>
      String(paragraph)
        .replace(
          /{{patientName}}/g,
          header?.patientName || "________________"
        )
        .replace(
          /{{opdIpdNo}}/g,
          header?.opdIpdNo || "______"
        )
        .replace(
          /{{doctorName}}/g,
          header?.doctorName || "________________"
        )
    );

  const handleAdd = () => {
    if (!activeEntry.procedureName.trim()) {
      alert("Please enter the procedure name.");
      return;
    }
    if (!activeEntry.takenBy.trim()) {
      alert("Please enter the name of the person who took consent.");
      return;
    }
    
    setStagedRecords((prev) => [...prev, { id: `staged_${Date.now()}`, ...activeEntry }]);
    setActiveEntry(emptyEntry());
  };

  const removeStagedRecord = (id) => setStagedRecords((prev) => prev.filter((r) => r.id !== id));

  const saveRow = (claSS, notes) =>
    new Promise((resolve, reject) => {
      const existingId = existingRowIdsRef.current[claSS];
      dispatch(
        patientActions.saveProcedureField(
          {
            ...(existingId != null ? { id: existingId } : {}),
            claSS,
            notes: notes != null ? String(notes) : "",
            patientId,
            visitId,
            groupname,
            subgroup,
          },
          (res) => {
            if (
              typeof res === "function" ||
              typeof res === "string" ||
              res?.errorMessages?.length
            ) {
              reject(
                new Error(
                  res?.errorMessages?.[0] ||
                    (typeof res === "string" ? res : "Unable to save the consent form."),
                ),
              );
              return;
            }

            const returnedId = res?.procedure?.id;
            if (returnedId != null) existingRowIdsRef.current[claSS] = returnedId;
            resolve(res);
          }
        )
      );
    });

  const getTemplateMetadata = () => ({
    templateMetadata: {
      EN: {
        templateId: templates.en.id,
        version: templates.en.version,
      },
      KN: {
        templateId: templates.kn.id,
        version: templates.kn.version,
      },
      selectedLanguage: language.toUpperCase(), 
    },
  });

  const handleSave = async () => {
    if (formStatus === ConsentFormStatus.completed) {
      alert("This consent form is completed and cannot be edited.");
      return false;
    }

    if (stagedRecords.length === 0 && persistedRecords.length === 0) {
      alert("Please add at least one procedure before saving.");
      return false;
    }
    setSaving(true);
    setSaveStatus("");
    try {
      let newlyPersisted = [];
      if (stagedRecords.length > 0) {
        if (!sharedSigRef.current || sharedSigRef.current.isEmpty()) {
          alert("Please capture the patient/relative signature before saving the new procedures.");
          setSaving(false);
          return false;
        }
        const dataUrl = sharedSigRef.current.toDataURL("image/png");
        newlyPersisted = stagedRecords.map((r) => ({
          procedureName: r.procedureName,
          takenBy: r.takenBy,
          date: r.date,
          signature: dataUrl,
        }));
      }
      const allRecords = [
        ...persistedRecords.map((r) => ({
          procedureName: r.procedureName,
          takenBy: r.takenBy,
          date: r.date,
          signature: r.signature,
        })),
        ...newlyPersisted,
      ];
      await saveRow("PatientName", header.patientName);
      await saveRow("OpdIpdNo", header.opdIpdNo);
      await saveRow("TreatingDoctor", header.doctorName);
      await saveRow("status", ConsentFormStatus.inprogress);
      await saveRow("PanchakarmaProcedures", JSON.stringify(allRecords));
      await saveRow(
        "ConsentTemplateMetadata",
        JSON.stringify(getTemplateMetadata())
      );

      if (newlyPersisted.length > 0) {
        setPersistedRecords((prev) => [
          ...prev,
          ...newlyPersisted.map((r) => ({
            ...r,
            sigUrl: r.signature,
          })),
        ]);

        setStagedRecords([]);
        sharedSigRef.current.clear();
      }
      setSaveStatus("saved");
      setAlreadySaved(true);
      return true;
    } catch (e) {
      console.error("Panchakarma consent save failed:", e);
      setSaveStatus("error");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = async () => {
    if (saving) return;

    if (!alreadySaved) {
      const saved = await handleSave();
      if (!saved) return;
    }

    setShowPrint(true);
  };

  const crumbs = [
  { label: `${(header.patientName || "").toUpperCase()} ${header.age} ${header.gender}`.trim(),
    path: `/PatientSections/${patientId}/${visitId}`, 
  },
  { label: "Consent Forms",
    path: `/PatientSections/${patientId}/${visitId}/consent-forms`
  },
  { label: "Panchakarma Consent Form" },
  ]; 

  const cardStyle = {
    background: "#fff",
    border: "1px solid #e3e6ea",
    borderRadius: 10,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    padding: 20,
    marginBottom: 16,
  };

  const combinedRows = [
    ...persistedRecords.map((r) => ({ ...r, pending: false })),
    ...stagedRecords.map((r) => ({ ...r, pending: true })),
  ];
  const isCompleted = formStatus === ConsentFormStatus.completed;

  return (
    <div className="mainsection">
      <Breadcrumbs crumbs={crumbs} />

      {isCompleted && (
        <div className="alert alert-info py-2" role="status">
          This consent form is completed and is available in read-only mode.
        </div>
      )}

      <fieldset
        disabled={isCompleted}
        style={{
          border: 0,
          margin: 0,
          minWidth: 0,
          padding: 0,
          pointerEvents: isCompleted ? "none" : "auto",
        }}
      >
      <div className="row mt-2">
        <div className="col-md-4 mb-3">
          <div style={cardStyle}>
            <h6 className="mb-3">Add Procedure</h6>
            <div className="mb-2">
              <label style={{ fontSize: 12, color: "#666" }}>Name of Procedure (ಕಾರ್ಯವಿಧಾನದ ಹೆಸರು)</label>
              <input
                className="form-control"
                value={activeEntry.procedureName}
                onChange={(e) => setActiveEntry((p) => ({ ...p, procedureName: e.target.value }))}
              />
            </div>
            <div className="mb-2">
              <label style={{ fontSize: 12, color: "#666" }}>Consent Taken By (ಒಪ್ಪಿಗೆಯನ್ನು ತೆಗೆದುಕೊಂಡವರ ಹೆಸರು)</label>
              <input
                className="form-control"
                value={activeEntry.takenBy}
                onChange={(e) => setActiveEntry((p) => ({ ...p, takenBy: e.target.value }))}
              />
            </div>
            <div className="mb-3">
              <label style={{ fontSize: 12, color: "#666" }}>Date (ದಿನಾಂಕ)</label>
              <input className="form-control" value={activeEntry.date} readOnly disabled />
            </div>
            <button type="button" className="btn btn-primary btn-sm w-100" onClick={handleAdd}>
              + Add
            </button>
          </div>
        </div>

        <div className="col-md-8 mb-3">
          <div style={cardStyle}>
            <div
              role="group"
              aria-label="Select language"
              style={{ display: "inline-flex", border: "1px solid #1a73e8", borderRadius: 6, overflow: "hidden", marginBottom: 12 }}
            >
              <button
                type="button"
                onClick={() => setLanguage("en")}
                style={{
                  padding: "3px 12px",
                  fontSize: 12,
                  border: "none",
                  cursor: "pointer",
                  background: language === "en" ? "#1a73e8" : "#fff",
                  color: language === "en" ? "#fff" : "#1a73e8",
                  fontWeight: 500,
                }}
              >
                English
              </button>
              <button
                type="button"
                onClick={() => setLanguage("kn")}
                style={{
                  padding: "3px 12px",
                  fontSize: 12,
                  border: "none",
                  borderLeft: "1px solid #1a73e8",
                  cursor: "pointer",
                  background: language === "kn" ? "#1a73e8" : "#fff",
                  color: language === "kn" ? "#fff" : "#1a73e8",
                  fontWeight: 500,
                }}
              >
                ಕನ್ನಡ
              </button>
            </div>

            <div lang={language}>
              {templateLoading ? (
                <p>Loading consent form text...</p>
              ) : templateError ? (
                <p className="text-danger">{templateError}</p>
              ) : (
                fillPanchakarmaTemplate(
                  language === "en" ? templates.en.content : templates.kn.content
                ).map((paragraph, index) => (
                  <p key={index} style={{ textAlign: "justify" }}>
                    {paragraph}
                  </p>
                ))
              )}
            </div>
          </div>

          <div style={cardStyle}>
            <h6 className="mb-3" style={{ fontWeight: 600, color: "#333" }}>Saved Procedures</h6>
            {combinedRows.length === 0 ? (
              <div className="text-muted text-center py-3" style={{ background: "#f8f9fa", borderRadius: 6, fontSize: 13 }}>
                No procedures added yet.
              </div>
            ) : (
              <div className="table-responsive" style={{ border: "1px solid #dee2e6", borderRadius: 6, overflow: "hidden" }}>
                <table className="table table-bordered table-hover align-middle mb-0" style={{ fontSize: 13 }}>
                  <thead style={{ background: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
                    <tr>
                      <th className="text-center" style={{ width: "60px" }}>Sl. No.</th>
                      <th>Procedure Name (ಕಾರ್ಯವಿಧಾನದ ಹೆಸರು)</th>
                      <th>Consent Taken By (ಒಪ್ಪಿಗೆಯನ್ನು ತೆಗೆದುಕೊಂಡವರ ಹೆಸರು)</th>
                      <th className="text-center" style={{ width: "110px" }}>Date (ದಿನಾಂಕ)</th>
                      <th className="text-center" style={{ width: "120px" }}>Signature (ಸಹಿ)</th>
                      <th className="text-center" style={{ width: "90px" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {combinedRows.map((r, i) => (
                      <tr key={r.id || `persisted_${i}`}>
                        <td className="text-center fw-bold">{i + 1}</td>
                        <td style={{ fontWeight: 500 }}>{r.procedureName}</td>
                        <td>{r.takenBy || <span className="text-muted">—</span>}</td>
                        <td className="text-center">
                          {helperServices.formatDateOnly(r.date, true) || (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td className="text-center">
                          {r.pending ? (
                            <span className="badge bg-warning text-dark" style={{ fontSize: 10 }}>Pending</span>
                          ) : r.sigUrl ? (
                            <img src={r.sigUrl} alt="signature" style={{ maxHeight: 32, objectFit: "contain" }} />
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td className="text-center">
                          {r.pending ? (
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              style={{ fontSize: 11, padding: "2px 8px" }}
                              onClick={() => removeStagedRecord(r.id)}
                            >
                              Remove
                            </button>
                          ) : (
                            <span className="badge bg-light text-secondary border" style={{ fontSize: 10 }}>Saved</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>   

          <div style={cardStyle}>
            <h6 className="mb-2">Patient / Relative Signature (ರೋಗಿಯ / ಸಂಬಂಧಿ ಸಹಿ)</h6>
            <div className="text-muted mb-2" style={{ fontSize: 12 }}>
              This signature will apply to all newly added procedures above once saved.
            </div>
            <SignaturePad ref={sharedSigRef} label="" />
          </div>
        </div>
      </div>
      </fieldset>

      <div
        style={{
          position: "fixed",
          bottom: 24,
          right: 24,
          display: "flex",
          alignItems: "center",
          gap: 12,
          background: "#ffffff",
          padding: "10px 18px",
          borderRadius: 8,
          boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
          zIndex: 200,
          border: "1px solid #e2e8f0",
        }}
      >
        {saveStatus === "saved" && <span className="text-success fw-bold" style={{ fontSize: 13 }}>✓ Saved</span>}
        {saveStatus === "error" && <span className="text-danger fw-bold" style={{ fontSize: 13 }}>✕ Save failed</span>}
        <button className="btn btn-outline-primary btn-sm" onClick={handlePrint} disabled={saving}>
          Print
        </button>
        {!isCompleted && (
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : alreadySaved ? "Update" : "Save"}
          </button>
        )}
      </div>

      {showPrint && (
        <PrintPanchkarmaConsent
          patientInfo={{
            fullName: header.patientName,
            ipopnumber: header.opdIpdNo,
            doctorName: header.doctorName,
            procedures: persistedRecords.map((r) => ({
              procedureName: r.procedureName,
              takenBy: r.takenBy,
              date: r.date,
              signature: r.signature || r.sigUrl || null,
            })),
            patientId,
            visitId,
          }}
          parentCallBack={() => setShowPrint(false)}
        />
      )}
    </div>
  );
};

export default PanchakarmaConsentForm;
